/**
 * dashboard/classes/$classId/quiz/index.tsx — Tutor activate flow (Phase G.3).
 *
 * Lists the library quizzes that match the class sd_grade (via
 * GET /api/quiz/activatable?classId=X — handleTutorListActivatable
 * from Phase E.2). Each row has an "Aktifkan" button that opens a
 * modal for selecting the timer (10/20/30/45/60 seconds — the
 * VALID_TIMERS set from quiz.activation.ts).
 *
 * On confirm: POST /api/quiz/activate → navigate to the live
 * control page at /dashboard/classes/$classId/quiz/$activationId/live.
 *
 * v0.1.4: modal aktivasi diekstrak ke <QuizActivateModal> supaya
 * bisa dipakai ulang oleh tab Quiz di ClassContentToggle.
 */

import { type ReactNode, useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../../../components/ui/StatePanel.js';
import { classDetailQuery, freshSessionQuery } from '../../../../../lib/api/queries.js';
import { QuizActivateModal } from '../../../../../components/tutor/QuizActivateModal.js';
import { LEARNING_LEVEL_LABEL, toLearningLevelEnum } from '../../../../../lib/leveling.js';

function levelBadge(sdGrade: number): string {
  const lv = toLearningLevelEnum(sdGrade);
  return lv ? `${lv} ${LEARNING_LEVEL_LABEL[lv]}` : '—';
}

export const Route = createFileRoute('/dashboard/classes/$classId/quiz/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: TutorActivatePage,
});

interface ActivatableQuiz {
  id: string;
  title: string;
  description: string | null;
  sd_grade: number;
  source: 'library' | 'custom' | 'library_pending';
  updated_at: string;
}

function TutorActivatePage(): ReactNode {
  const { classId } = useParams({ strict: false }) as { classId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedQuiz, setSelectedQuiz] = useState<ActivatableQuiz | null>(null);

  const query = useQuery({
    queryKey: ['quiz', 'activatable', { classId }],
    queryFn: async () => {
      const r = await fetch(`/api/quiz/activatable?classId=${encodeURIComponent(classId)}`, { credentials: 'include' });
      const j = (await r.json()) as { ok: boolean; data?: { quizzes: ActivatableQuiz[] }; error?: { message: string } };
      if (!j.ok || !j.data) throw new Error(j.error?.message ?? 'Gagal memuat daftar quiz');
      return j.data.quizzes;
    },
  });

  // Audit F11: show the class NAME, not its raw id (CLASS-<uuid>).
  const classDetail = useQuery(classDetailQuery(classId));
  const className = classDetail.data?.ok ? (classDetail.data.data.name ?? null) : null;

  return (
    <main className="page-shell" data-testid="activate-page">
      <PageHeader
        eyebrow={className ? `Kelas · ${className}` : 'Kelas'}
        title="Aktifkan Quiz"
        description="Pilih quiz dari library untuk diaktifkan live. Siswa di kelas ini akan bisa join begitu quiz dimulai."
        action={
          <Link to="/dashboard/quizzes" className="button button-secondary">
            Quiz Saya
          </Link>
        }
      />

      {query.isLoading ? (
        <StatePanel variant="empty" title="Memuat…" />
      ) : query.isError ? (
        <StatePanel
          variant="error"
          title="Gagal memuat"
          description={(query.error as Error).message}
        />
      ) : !query.data || query.data.length === 0 ? (
        <StatePanel
          variant="empty"
          title="Tidak ada quiz untuk kelas ini"
          description="Kelas ini belum punya quiz library yang sesuai levelnya. Minta admin menambahkan quiz untuk level ini, atau buat kustom di tab Quiz Saya."
        />
      ) : (
        <ul className="quiz-list" data-testid="activatable-list">
          {query.data.map((q) => (
            <li key={q.id} className="card quiz-list-item">
              <div className="quiz-list-meta">
                <span className="card-meta">{levelBadge(q.sd_grade)}</span>
              </div>
              <h3 className="card-title">{q.title}</h3>
              {q.description ? <p className="quiz-list-desc">{q.description}</p> : null}
              <div className="cluster">
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => setSelectedQuiz(q)}
                  data-testid="activate-btn"
                >
                  Aktifkan
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {selectedQuiz ? (
        <QuizActivateModal
          quizId={selectedQuiz.id}
          quizTitle={selectedQuiz.title}
          classId={classId}
          onClose={() => setSelectedQuiz(null)}
          onActivated={async (activationId) => {
            setSelectedQuiz(null);
            await queryClient.invalidateQueries({ queryKey: ['quiz', 'activation'] });
            await navigate({
              to: '/dashboard/classes/$classId/quiz/$activationId/live',
              params: { classId, activationId },
              search: { from: undefined },
            });
          }}
        />
      ) : null}
    </main>
  );
}
